import { Button, Table, Tooltip, Whisper } from 'rsuite';
import noImage from '../../../../../../assets/icons/no-image.svg';
import { MdSwapCalls } from 'react-icons/md';

import { TABLE_SORTABLE_COLUMNS } from '../../constants';
import type { VisaoGeralElasticidadeTableResponse, VisaoGeralElasticidadeTableRowData } from '../../types';
import { MarketSharePotentialGain } from '../MarketSharePotentialGain';
import styles from './ProductCompetitivenessTable.module.scss';
import { formatDecimal2, formatPercent1Decimal } from '../../utils';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useVisaoGeralElasticidadeStore } from '../../VisaoGeralElasticidade.store';
import { useElasticidadeCruzadaStore } from '../../../ElasticidadeCruzada/ElasticidadeCruzada.store';
import infoIcon from '../../../../../../assets/icons/icon_info_gray.svg';

const { Column, HeaderCell, Cell } = Table;
const { Pagination } = Table;

const ELASTICIDADE_CRUZADA_COLORS: Record<string, string> = {
  Elástico: '#378EF0',
  Inelástico: '#bcbcbc',
  'Pouco elástico': '#A5CCF8',
  'Muito elástico': '#0D66D0',
};

type SortType = {
  column?: string;
  orderBy?: 'asc' | 'desc' | undefined;
};

type ProductCompetitivenessTableProps = {
  tableData: VisaoGeralElasticidadeTableResponse;
  onChangePage: (page: number, size: number) => void;
  onChangeLength: (size: number) => void;
  onSortColumn: (column: string, sortType: 'asc' | 'desc' | undefined) => void;
};

const getElasticityColor = (elasticityLevel: string) => ELASTICIDADE_CRUZADA_COLORS[elasticityLevel] ?? '#bcbcbc';

const ProductNameCell = ({ descricao }: { descricao?: string }) => {
  const content = (
    <div className={styles['product-reference']}>
      <span className={styles['product-reference-name']}>{descricao}</span>
    </div>
  );
  if (!descricao) {
    return content;
  }
  return (
    <Whisper delayShow={0.2} trigger="hover" placement="top" speaker={<Tooltip>{descricao}</Tooltip>}>
      {content}
    </Whisper>
  );
};

export const ProductCompetitivenessTable = ({ tableData, onChangePage, onChangeLength, onSortColumn }: ProductCompetitivenessTableProps) => {
  const [length, setLength] = useState(20);
  const [sort, setSort] = useState<SortType>({});
  const history = useHistory();
  const setFiltersFromVisaoGeralElasticidade = useElasticidadeCruzadaStore(state => state.setFiltersFromVisaoGeralElasticidade);
  const marketShareOption = useVisaoGeralElasticidadeStore(state => state.marketShareOption);
  const { segmentation, subSegmentation, region } = useVisaoGeralElasticidadeStore(state => state.tableBody);
  const isSalesRevenue = marketShareOption === 'valor-vendas';

  const handleLengthChange = (size: number) => {
    setLength(size);
    if (onChangeLength) {
      onChangeLength(size);
    }
  };

  const handlePageChange = (page: number) => {
    onChangePage?.(page, length);
  };

  const handleSortColumn = (dataKey: string, sortType: 'asc' | 'desc' | undefined) => {
    const sortObj = { column: dataKey, orderBy: sortType };
    setSort(sortObj);
    onSortColumn?.(dataKey, sortType);
  };

  const handleOpenAnalysis = (rowData: VisaoGeralElasticidadeTableRowData) => {
    setFiltersFromVisaoGeralElasticidade(rowData, { segmentation, subSegmentation, region });
    history.push('/isa/elasticidade-cruzada');
  };

  return (
    <div className={styles['table-wrapper']}>
      <Table
        className={styles['competitiveness-table']}
        autoHeight
        headerHeight={48}
        data={tableData?.content ?? []}
        bordered
        sortColumn={sort.column}
        sortType={sort.orderBy}
        onSortColumn={handleSortColumn}
        rowKey="referenceProductGtin"
        rowHeight={100}
      >
        <Column flexGrow={1.7} sortable>
          <HeaderCell>Produto referência</HeaderCell>
          <Cell dataKey={TABLE_SORTABLE_COLUMNS.referenceProduct}>
            {(rowData: VisaoGeralElasticidadeTableRowData) => (
              <div className={styles['product-reference-wrapper']}>
                <img
                  src={rowData.referenceProduct?.imagem ?? noImage}
                  alt={rowData.referenceProductDescricao}
                  onError={e => {
                    (e.target as HTMLImageElement).src = noImage;
                  }}
                />
                <div className={styles['product-reference-content']}>
                  <div className={styles['badge-wrapper']}>
                    <div className={styles.badge}>{rowData.referenceProductCategoria}</div>
                    <div className={styles.badge}>{rowData.referenceProductSecao}</div>
                  </div>
                  <ProductNameCell descricao={rowData.referenceProductDescricao} />
                  <div className={styles.gtin}>
                    <span>{rowData.referenceProductGtin}</span>
                    <span className={styles['gtin-fabricante']}>{rowData.referenceProductFabricante}</span>
                  </div>
                </div>
              </div>
            )}
          </Cell>
        </Column>
        <Column flexGrow={1.5} sortable>
          <HeaderCell>Top produto concorrente</HeaderCell>
          <Cell dataKey={TABLE_SORTABLE_COLUMNS.topCompetitor}>
            {(rowData: VisaoGeralElasticidadeTableRowData) => (
              <div className={styles['product-cell-content']}>
                <div className={styles['badge-wrapper']}>
                  <div className={styles.badge}>{rowData.topCompetitorCategoria}</div>
                  <div className={styles.badge}>{rowData.topCompetitorSecao}</div>
                </div>
                <ProductNameCell descricao={rowData.topCompetitorDescricao} />
                <div className={styles.gtin}>
                  <span>{rowData.topCompetitorGtin}</span>
                  <span className={styles['gtin-fabricante']}>{rowData.topCompetitorFabricante}</span>
                </div>
              </div>
            )}
          </Cell>
        </Column>
        <Column flexGrow={1} sortable>
          {/* <Column width={150} sortable> */}
          <HeaderCell>Elasticidade cruzada</HeaderCell>
          <Cell dataKey={TABLE_SORTABLE_COLUMNS.salesVolumeElasticityLevel}>
            {(rowData: VisaoGeralElasticidadeTableRowData) => (
              <Whisper
                delayShow={0.2}
                trigger="hover"
                placement="right"
                speaker={<Tooltip>{formatDecimal2(isSalesRevenue ? rowData.salesRevenueElasticity : rowData.salesVolumeElasticity)}</Tooltip>}
              >
                <div className={styles['elasticidade-cruzada-content']}>
                  <MdSwapCalls
                    size={20}
                    color={getElasticityColor(isSalesRevenue ? rowData.salesRevenueElasticityLevel : rowData.salesVolumeElasticityLevel)}
                  />
                  <span className={styles['elasticidade-cruzada-text']}>
                    {isSalesRevenue ? rowData.salesRevenueElasticityLevel : rowData.salesVolumeElasticityLevel}
                  </span>
                </div>
              </Whisper>
            )}
          </Cell>
        </Column>
        <Column flexGrow={1} sortable>
          {/* <Column width={140} sortable> */}
          <HeaderCell>
            <div className={styles['header-double-text-group']}>
              <p className={styles['header-double-text']}>Competitividade</p>
              <p className={styles['header-double-text']}>Infopanel atual</p>
            </div>
          </HeaderCell>
          <Cell dataKey={TABLE_SORTABLE_COLUMNS.salesVolumeCurrentCpi}>
            {(rowData: VisaoGeralElasticidadeTableRowData) => (
              <span className={styles['competitividade-text']}>
                {formatPercent1Decimal(isSalesRevenue ? rowData.salesRevenueCurrentCpi : rowData.salesVolumeCurrentCpi)}
              </span>
            )}
          </Cell>
        </Column>
        {/* <Column width={140} sortable>
          <HeaderCell>
            <span className={styles['header-double-text-group']}>
              <p className={styles['header-double-text']}>Competitividade</p>
              <p className={styles['header-double-text']}>ótima</p>
            </span>
          </HeaderCell>
          <Cell dataKey={TABLE_SORTABLE_COLUMNS.salesVolumeOptimalCpi}>
            {(rowData: VisaoGeralElasticidadeTableRowData) => (
              <span className={styles['competitividade-text']}>
                {formatPercent1Decimal(isSalesRevenue ? rowData.salesRevenueOptimalCpi : rowData.salesVolumeOptimalCpi)}
              </span>
            )}
          </Cell>
        </Column>
        <Column flexGrow={1} minWidth={180} sortable>
          <HeaderCell>
            <span className={styles['header-double-text-group']}>
              <div>
                <span className={styles['header-double-text']}>Potencial de ganho</span>
                <Whisper
                  trigger="hover"
                  placement="right"
                  speaker={<Tooltip>Potencial de ganho de market share estimado ao atingir a competitividade ótima</Tooltip>}
                >
                  <img src={infoIcon} alt="Info" className={styles['info-icon']} />
                </Whisper>
              </div>
            </span>
          </HeaderCell>
          <Cell dataKey={TABLE_SORTABLE_COLUMNS.salesVolumePotentialGain}>
            {(rowData: VisaoGeralElasticidadeTableRowData) => (
              <MarketSharePotentialGain value={isSalesRevenue ? rowData.salesRevenuePotentialGain : rowData.salesVolumePotentialGain} />
            )}
          </Cell>
        </Column> */}
        <Column>
          <HeaderCell />
          <Cell>
            {(rowData: VisaoGeralElasticidadeTableRowData) => (
              <div className={styles['buttons-wrapper']}>
                <Button appearance="subtle" size="sm" onClick={() => handleOpenAnalysis(rowData)}>
                  Abrir análise
                </Button>
                {/* <Button appearance="subtle" size="sm">
                  Criar novo monitoramento
                </Button> */}
              </div>
            )}
          </Cell>
        </Column>
      </Table>
      <Pagination
        className={styles['requests-pagination']}
        activePage={tableData?.page?.number + 1}
        displayLength={tableData?.page?.size}
        total={tableData?.page?.totalElements}
        onChangePage={handlePageChange}
        onChangeLength={handleLengthChange}
        renderTotal={() => `Exibindo ${tableData?.page?.size} de ${tableData?.page?.totalElements} produtos.`}
      />
    </div>
  );
};
